"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { useEffect, useState } from "react";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { setQueryClientRef } from "@/lib/auth-store";

import { SimulationBanner } from "@/components/layout/simulation-banner";

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  // Register queryClient with auth store so logout can clear all cached data
  useEffect(() => {
    setQueryClientRef(queryClient);
  }, [queryClient]);

  // Support automated testing / screenshot capture via query param
  if (typeof window !== "undefined") {
    const searchParams = new URLSearchParams(window.location.search);
    const qToken = searchParams.get("auth_token");
    if (qToken && localStorage.getItem("playcampus_access_token") !== qToken) {
      localStorage.setItem("playcampus_access_token", qToken);
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem disableTransitionOnChange>
        <TooltipProvider delay={200}>
          <SimulationBanner />
          {children}
          <Toaster position="top-right" richColors closeButton />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
